/**
 * Cross-tab auth lifecycle coordination (#866).
 *
 * Every browser tab runs its OWN React Query cache, so a logout in one tab
 * leaves sibling tabs holding the previous user's private queries (favorites,
 * profile, settings, market purchases, account-info with staleTime: Infinity,
 * ...) for up to gcTime (30m). BroadcastChannel lets any tab tell every other
 * tab "the auth identity changed — purge your cache". The message carries only
 * a purge marker + generation + sender tab id, NEVER credentials or user data.
 */
import type { QueryClient } from '@tanstack/react-query';
import { clearCsrfToken } from '@/lib/auth/csrfToken';
import type { UserSession } from '@/lib/server-fns/auth-fns';

const AUTH_PURGE_CHANNEL = 'til:auth-purge';
const AUTH_PURGE_MESSAGE = 'auth-purge';

/**
 * Storage keys that carry user content the QueryClient purge cannot reach.
 * Owned here (not next to their writers) because the purge must know every
 * key that survives a logout: the community post draft holds the previous
 * user's unpublished caption + uploaded image URLs (COR-05), and the checkout
 * idempotency keys are per-intent scratch that must not leak across users.
 */
export const COMMUNITY_POST_DRAFT_KEY = 'community_post_draft_v2';
const CHECKOUT_IDEMPOTENCY_PREFIX = 'til:checkout:';

// Tab-scoped id so a tab never reacts to its own broadcast. Real
// BroadcastChannel doesn't deliver to the sender, but some engines/polyfills
// do — without this guard a tab would echo its own purge forever.
const TAB_ID =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

let generation = 0;
let channel: BroadcastChannel | null | undefined;

function getChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined') return null;
  if (channel !== undefined) return channel;
  const BroadcastChannelCtor = globalThis.BroadcastChannel;
  if (!BroadcastChannelCtor) {
    channel = null;
    return channel;
  }
  try {
    channel = new BroadcastChannelCtor(AUTH_PURGE_CHANNEL);
  } catch {
    // ponytail: no BroadcastChannel (older engines) — same-tab purge still
    // works, siblings just aren't told. Add a storage-event fallback if
    // supporting those engines matters.
    channel = null;
  }
  return channel;
}

/**
 * Purge ALL cached queries + CSRF state for the current tab. A full clear is
 * deliberate: enumerating user-scoped keys is brittle (a new query silently
 * re-introduces the leak), while public queries simply refetch on next use.
 * The session is explicitly re-marked null so useIsAuthenticated() flips
 * immediately and route guards redirect.
 */
export function purgeAuthState(queryClient: QueryClient): void {
  queryClient.clear();
  queryClient.setQueryData<UserSession | null>(['auth', 'session'], null);
  clearCsrfToken();
  purgeUserContentFromStorage();
}

/**
 * COR-05: clear web storage the query-cache purge misses. The create-post
 * draft (localStorage) and checkout idempotency keys (sessionStorage) would
 * otherwise survive logout and user switches on a shared device.
 */
function purgeUserContentFromStorage(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(COMMUNITY_POST_DRAFT_KEY);
    const stale: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(CHECKOUT_IDEMPOTENCY_PREFIX)) stale.push(key);
    }
    stale.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Storage unavailable (private mode / disabled): nothing else to purge.
  }
}

/**
 * Broadcast a purge event to sibling tabs. Carries only a marker, a generation
 * (so tests/observers can see a NEW event was sent) and the sender's tab id —
 * never credentials or user data.
 */
export function broadcastAuthPurge(): void {
  const ch = getChannel();
  if (!ch) return;
  generation += 1;
  ch.postMessage({ type: AUTH_PURGE_MESSAGE, generation, source: TAB_ID });
}

/**
 * Subscribe to purge events from sibling tabs. Each tab runs its own cache, so
 * the handler must purge THAT tab's QueryClient. Returns an unsubscribe fn.
 */
export function subscribeAuthPurge(handler: () => void): () => void {
  const ch = getChannel();
  if (!ch) return () => {};
  const listener = (event: MessageEvent) => {
    if (event.data && event.data.type === AUTH_PURGE_MESSAGE && event.data.source !== TAB_ID) {
      handler();
    }
  };
  ch.addEventListener('message', listener);
  return () => ch.removeEventListener('message', listener);
}
